import { defineMessages } from 'react-intl';

export const aiAdvisoryMessages = defineMessages({
  title: {
    id: 'aiAdvisory.title',
    defaultMessage: 'Estimation AI Advisory',
  },
  subtitle: {
    id: 'aiAdvisory.subtitle',
    defaultMessage: 'Get intelligent advice for your project estimations',
  },
  welcomeMessage: {
    id: 'aiAdvisory.welcomeMessage',
    defaultMessage:
      "Hello! I'm your AI Estimation Advisor. I can help you with project estimation, methodology selection, resource planning, and cost analysis. How can I assist you today?",
  },
  placeholder: {
    id: 'aiAdvisory.placeholder',
    defaultMessage: 'Ask me anything about project estimation...',
  },
  advisoryProvided: {
    id: 'aiAdvisory.advisoryProvided',
    defaultMessage: 'AI Advisory provided',
  },
  sendButton: {
    id: 'aiAdvisory.sendButton',
    defaultMessage: 'Send',
  },
  helpText: {
    id: 'aiAdvisory.helpText',
    defaultMessage: 'Press Enter to send, Shift+Enter for new line',
  },
  aiResponse: {
    id: 'aiAdvisory.aiResponse',
    defaultMessage:
      'Thank you for your question! This is a placeholder response. In the actual implementation, this would be connected to an LLM service to provide intelligent estimation advice.',
  },
  loadingChatHistory: {
    id: 'aiAdvisory.loadingChatHistory',
    defaultMessage: 'Loading chat history...',
  },
  failedToLoadChatHistory: {
    id: 'aiAdvisory.failedToLoadChatHistory',
    defaultMessage: 'Failed to load chat history',
  },
  generatingResponse: {
    id: 'aiAdvisory.generatingResponse',
    defaultMessage: 'Generating response...',
  },
  // EWM-1121 - shown on an answer whose generation was cut short. The text that arrived is real,
  // so this marks it rather than replacing it.
  answerIncomplete: {
    id: 'aiAdvisory.answerIncomplete',
    defaultMessage: 'Generation was interrupted - this answer may be incomplete.',
  },
  // EWM-968 - shown instead of the generic label once the assistant reports what it is doing.
  activityThinking: {
    id: 'aiAdvisory.activityThinking',
    defaultMessage: 'Thinking...',
  },
  activityWebSearch: {
    id: 'aiAdvisory.activityWebSearch',
    defaultMessage: 'Searching the web...',
  },
  activityTool: {
    id: 'aiAdvisory.activityTool',
    defaultMessage: 'Working with {tool}...',
  },
  deleteConversation: {
    id: 'aiAdvisory.deleteConversation',
    defaultMessage: 'Delete Conversation',
  },
  deleteConversationTitle: {
    id: 'aiAdvisory.deleteConversationTitle',
    defaultMessage: 'Delete Conversation',
  },
  deleteConversationWarning: {
    id: 'aiAdvisory.deleteConversationWarning',
    defaultMessage:
      'This will permanently delete this conversation and all its messages. This action cannot be undone.',
  },
  deleteConversationConfirm: {
    id: 'aiAdvisory.deleteConversationConfirm',
    defaultMessage: 'Delete Conversation',
  },
  shareConversation: {
    id: 'aiAdvisory.shareConversation',
    defaultMessage: 'Share Conversation',
  },
  convertToEstimation: {
    id: 'aiAdvisory.convertToEstimation',
    defaultMessage: 'Convert to Estimation',
  },
  convertNoWorkBreakdown: {
    id: 'aiAdvisory.convertNoWorkBreakdown',
    defaultMessage: 'This answer contains no work breakdown, so it cannot be converted.',
  },
  shareConversationTitle: {
    id: 'aiAdvisory.shareConversationTitle',
    defaultMessage: 'Share AI Advisory',
  },
  shareConversationDescription: {
    id: 'aiAdvisory.shareConversationDescription',
    defaultMessage: 'Send a link to this conversation to someone in your tenant.',
  },
  shareConversationEmailLabel: {
    id: 'aiAdvisory.shareConversationEmailLabel',
    defaultMessage: 'Recipient',
  },
  shareConversationRecipientPlaceholder: {
    id: 'aiAdvisory.shareConversationRecipientPlaceholder',
    defaultMessage: 'Select user',
  },
  shareConversationSend: {
    id: 'aiAdvisory.shareConversationSend',
    defaultMessage: 'Send',
  },
  shareConversationCopyLink: {
    id: 'aiAdvisory.shareConversationCopyLink',
    defaultMessage: 'Copy link',
  },
  shareConversationCancel: {
    id: 'aiAdvisory.shareConversationCancel',
    defaultMessage: 'Cancel',
  },
  shareConversationSuccess: {
    id: 'aiAdvisory.shareConversationSuccess',
    defaultMessage: 'Invitation sent',
  },
  shareConversationError: {
    id: 'aiAdvisory.shareConversationError',
    defaultMessage: 'Failed to send invitation',
  },
  shareConversationNotAllowed: {
    id: 'aiAdvisory.shareConversationNotAllowed',
    defaultMessage: 'Recipient must be in the same tenant',
  },
  shareConversationNotFound: {
    id: 'aiAdvisory.shareConversationNotFound',
    defaultMessage: 'Conversation not found',
  },
  shareConversationEmailRequired: {
    id: 'aiAdvisory.shareConversationEmailRequired',
    defaultMessage: 'Please select a recipient',
  },
  linkCopied: {
    id: 'aiAdvisory.linkCopied',
    defaultMessage: 'Link copied to clipboard',
  },

  // EWM-534: indicator for conversations shared with the current user by someone else.
  sharedBadge: {
    id: 'aiAdvisory.sharedBadge',
    defaultMessage: 'Shared',
  },
  sharedBadgeTitle: {
    id: 'aiAdvisory.sharedBadgeTitle',
    defaultMessage: 'Shared with you by another user',
  },

  // EWM-521: Conversation folders
  newFolder: {
    id: 'aiAdvisory.newFolder',
    defaultMessage: 'New folder',
  },
  newFolderDefaultName: {
    id: 'aiAdvisory.newFolderDefaultName',
    defaultMessage: 'New Folder',
  },
  renameFolder: {
    id: 'aiAdvisory.renameFolder',
    defaultMessage: 'Rename folder',
  },
  renameFolderConfirm: {
    id: 'aiAdvisory.renameFolderConfirm',
    defaultMessage: 'Save',
  },
  renameFolderCancel: {
    id: 'aiAdvisory.renameFolderCancel',
    defaultMessage: 'Cancel',
  },
  renameFolderError: {
    id: 'aiAdvisory.renameFolderError',
    defaultMessage: 'Failed to rename folder',
  },
  createFolderError: {
    id: 'aiAdvisory.createFolderError',
    defaultMessage: 'Failed to create folder',
  },
  deleteFolder: {
    id: 'aiAdvisory.deleteFolder',
    defaultMessage: 'Delete folder',
  },
  deleteFolderTitle: {
    id: 'aiAdvisory.deleteFolderTitle',
    defaultMessage: 'Delete Folder',
  },
  deleteFolderWarningWithContents: {
    id: 'aiAdvisory.deleteFolderWarningWithContents',
    defaultMessage:
      'Delete folder "{name}"? The {count, plural, one {# conversation} other {# conversations}} inside will be moved to the unfoldered list. No conversation will be deleted.',
  },
  deleteFolderWarningEmpty: {
    id: 'aiAdvisory.deleteFolderWarningEmpty',
    defaultMessage: 'Delete folder "{name}"?',
  },
  deleteFolderConfirm: {
    id: 'aiAdvisory.deleteFolderConfirm',
    defaultMessage: 'Delete Folder',
  },
  moveToFolder: {
    id: 'aiAdvisory.moveToFolder',
    defaultMessage: 'Move to folder',
  },
  moveToFolderError: {
    id: 'aiAdvisory.moveToFolderError',
    defaultMessage: 'Failed to move conversation',
  },
  moveToFolderNoFolderOption: {
    id: 'aiAdvisory.moveToFolderNoFolderOption',
    defaultMessage: 'No folder',
  },
  moveToFolderCreateNew: {
    id: 'aiAdvisory.moveToFolderCreateNew',
    defaultMessage: '+ Create new folder',
  },

  // EWM-520: Conversation rename
  renameConversation: {
    id: 'aiAdvisory.renameConversation',
    defaultMessage: 'Rename conversation',
  },
  renameConversationConfirm: {
    id: 'aiAdvisory.renameConversationConfirm',
    defaultMessage: 'Save',
  },
  renameConversationCancel: {
    id: 'aiAdvisory.renameConversationCancel',
    defaultMessage: 'Cancel',
  },
  renameConversationError: {
    id: 'aiAdvisory.renameConversationError',
    defaultMessage: 'Failed to rename conversation',
  },

  // EWM-605: "ask again" could not recover the original question (conversation gone, or no
  // readable question in it). Reported rather than looking like a dead button.
  prefillQuestionUnavailable: {
    id: 'aiAdvisory.prefillQuestionUnavailable',
    defaultMessage:
      'The previous question could not be loaded. Type your question below to start a new advisory.',
  },

  // EWM-1182: PDF export runs in the browser; a render failure used to leave the page silent.
  pdfExportFailed: {
    id: 'aiAdvisory.pdfExportFailed',
    defaultMessage: 'The PDF could not be created. Please try again or export as Word.',
  },
});
